import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  DownloadSimple,
  FileText,
  FileZip,
  ArrowsClockwise,
  ClipboardText,
  EnvelopeSimple,
  ShieldCheck,
  Certificate,
  Spinner,
  UploadSimple,
} from '@phosphor-icons/react';
import api from '../services/api';
import { SendToCertPanel } from './certification/SendToCertPanel';
import { ProductionPreflightPanel } from './certification/ProductionPreflightPanel';
import { CertPhiStagingPanel } from './certification/CertPhiStagingPanel';
import { CertSourceAgencyLockBanner } from './certification/CertSourceAgencyLockBanner';
import { CertPopulationReview } from './certification/CertPopulationReview';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  validateCertCsv,
  checkFile,
  sanitizeFilename,
  MIN_INQUIRIES,
  MAX_FILE_BYTES,
} from './certification/certCsvValidation';

const PDF_MAX_BYTES = 20 * 1024 * 1024; // matches backend MAX_PDF_BYTES

const KIND_META = {
  readme: { icon: FileText, tone: 'slate', label: 'README' },
  email_body: { icon: EnvelopeSimple, tone: 'blue', label: 'Email' },
  test_plan: { icon: ShieldCheck, tone: 'emerald', label: 'Test plan' },
  edi_batch: { icon: Certificate, tone: 'amber', label: 'EDI' },
  test_run_report: { icon: ClipboardText, tone: 'blue', label: 'Report' },
  trace_map: { icon: ClipboardText, tone: 'slate', label: 'Trace' },
  other: { icon: FileText, tone: 'slate', label: 'File' },
};

const TONE_CLASSES = {
  slate: 'border-slate-200 bg-slate-50 text-slate-700',
  blue: 'border-blue-200 bg-blue-50 text-blue-700',
  emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  amber: 'border-amber-200 bg-amber-50 text-amber-700',
};

const formatBytes = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

const triggerBrowserDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 500);
};

const FileRow = ({ file, onDownload, isDownloading }) => {
  const meta = KIND_META[file.kind] || KIND_META.other;
  const Icon = meta.icon;
  const tone = TONE_CLASSES[meta.tone];
  return (
    <div
      className="flex items-center gap-3 py-2.5 px-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60 transition-colors"
      data-testid={`cert-file-row-${file.path}`}
    >
      <div className={`shrink-0 flex items-center justify-center w-9 h-9 rounded-lg border ${tone}`}>
        <Icon size={16} weight="duotone" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium text-slate-900 truncate">{file.name}</p>
          <Badge variant="outline" className="text-[10px] py-0 h-4 font-medium border-slate-200 text-slate-600">
            {meta.label}
          </Badge>
        </div>
        <p className="text-xs text-slate-500 truncate">{file.label}</p>
        <p className="text-[11px] text-slate-400 font-mono truncate">{file.path} · {formatBytes(file.size_bytes)}</p>
      </div>
      <Button
        size="sm"
        variant="outline"
        className="shrink-0"
        onClick={() => onDownload(file)}
        disabled={isDownloading}
        data-testid={`cert-download-file-${file.path}`}
      >
        {isDownloading ? <Spinner size={14} className="animate-spin" /> : <DownloadSimple size={14} />}
        <span className="ml-1.5">Download</span>
      </Button>
    </div>
  );
};

const SummaryRow = ({ label, value, tone }) => {
  const toneCls = tone === 'ok'
    ? 'text-emerald-700'
    : tone === 'bad'
      ? 'text-rose-600'
      : tone === 'warn'
        ? 'text-amber-700'
        : 'text-slate-800';
  return (
    <div className="flex justify-between gap-2 border-b border-slate-100 py-0.5">
      <dt className="text-slate-500">{label}</dt>
      <dd className={`font-medium ${toneCls}`} data-testid={`cert-summary-${String(label).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{value}</dd>
    </div>
  );
};

const CertificationPage = () => {
  const [files, setFiles] = useState([]);
  const [latestEdi, setLatestEdi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingPath, setDownloadingPath] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [usage, setUsage] = useState('T');
  const [mode, setMode] = useState('synthetic_structure');
  const [recipientsCsv, setRecipientsCsv] = useState('');
  const [regenOutput, setRegenOutput] = useState('');
  const [agencies, setAgencies] = useState([]);
  const [sourceAgencyId, setSourceAgencyId] = useState('');
  const [populationAttested, setPopulationAttested] = useState(false);
  const [csvFileName, setCsvFileName] = useState('');
  const [fileError, setFileError] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [review, setReview] = useState(null);
  const [preparing, setPreparing] = useState(false);
  const [reviewConfirmed, setReviewConfirmed] = useState(false);
  const [exclusionsAcknowledged, setExclusionsAcknowledged] = useState(false);
  const [retention, setRetention] = useState(null);
  const [sourceLock, setSourceLock] = useState(null);

  const loadFiles = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/certification/files');
      setFiles(data.files || []);
      setLatestEdi(data.latest_edi_batch || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load certification files');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/agencies/', { params: { status: 'active', limit: 100 } });
        setAgencies(data.agencies || []);
      } catch {
        /* selector stays empty — the server enforces agency validity anyway */
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/cert-staging/retention');
        setRetention(data);
      } catch {
        /* summary shows NOT APPROVED / UNHEALTHY; the server still enforces */
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/certification/source-agency-lock');
        setSourceLock(data);
        if (data?.locked && data.record?.found) setSourceAgencyId(data.agency_id);
      } catch {
        /* banner hidden; the server enforces the lock on every run */
      }
    })();
  }, []);

  const csvPresent = Boolean(recipientsCsv.trim());
  const sourcePresent = csvPresent || Boolean(pdfFile);
  const realMember = mode === 'real_member';

  useEffect(() => {
    if (realMember && usage !== 'T') setUsage('T');
  }, [realMember, usage]);

  // Any change to the source or the agency invalidates the reviewed population.
  const clearReview = useCallback(() => {
    setReview(null);
    setReviewConfirmed(false);
    setExclusionsAcknowledged(false);
  }, []);
  useEffect(() => { clearReview(); }, [recipientsCsv, pdfFile, sourceAgencyId, mode, clearReview]);

  const lockBlocksSelection = Boolean(
    sourceLock?.locked && sourceAgencyId && sourceAgencyId !== sourceLock.agency_id
      && (realMember || sourceLock.mandatory),
  );
  // REAL MEMBER runs are bound to the locked production record in EVERY
  // environment: if that record is absent / not bound here, the server refuses.
  const lockRecordUnbound = Boolean(
    realMember && sourceLock && (!sourceLock.locked || !(sourceLock.record && sourceLock.record.bound)),
  );

  const handleDownload = useCallback(async (file) => {
    setDownloadingPath(file.path);
    try {
      const response = await api.get('/certification/download', {
        params: { path: file.path },
        responseType: 'blob',
      });
      triggerBrowserDownload(response.data, file.name);
      toast.success(`Downloaded ${file.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to download ${file.name}`);
    } finally {
      setDownloadingPath(null);
    }
  }, []);

  const handleDownloadAll = useCallback(async () => {
    setDownloadingPath('__all__');
    try {
      const response = await api.get('/certification/download-all', { responseType: 'blob' });
      triggerBrowserDownload(response.data, 'odm_270_certification_package.zip');
      toast.success('Downloaded certification package (.zip)');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download package');
    } finally {
      setDownloadingPath(null);
    }
  }, []);

  const handleRegenerate = useCallback(async () => {
    setRegenerating(true);
    setRegenOutput('');
    try {
      const { data } = await api.post('/certification/regenerate', {
        mode,
        usage,
        recipients_csv: realMember ? review?.canonical_csv || null : null,
        source_agency_id: sourceAgencyId || null,
        population_attestation: realMember ? populationAttested : false,
        review_token: realMember ? review?.review_token || null : null,
        source_format: realMember ? review?.source_format || null : null,
        exclusions_acknowledged: realMember ? Boolean(review?.requires_exclusions && exclusionsAcknowledged) : false,
        excluded_rows: realMember && review?.requires_exclusions
          ? (review.excluded_rows || []).map((e) => ({ ref: e.ref, reasons: e.reasons }))
          : null,
      });
      setRegenOutput(data.stdout || '');
      toast.success(realMember ? 'REAL MEMBER CERT batch staged from the reviewed population' : 'Synthetic structure batch generated');
      if (realMember) {
        clearReview();
        setRecipientsCsv('');
        setPdfFile(null);
        setCsvFileName('');
      }
      await loadFiles();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = typeof detail === 'string'
        ? detail
        : detail?.message || (Array.isArray(detail) ? 'Request refused — check the required fields.' : 'Regeneration failed');
      toast.error(message);
    } finally {
      setRegenerating(false);
    }
  }, [mode, realMember, usage, review, sourceAgencyId, populationAttested, exclusionsAcknowledged, loadFiles, clearReview]);

  const handlePrepare = useCallback(async () => {
    setPreparing(true);
    clearReview();
    try {
      const form = new FormData();
      form.append('source_agency_id', sourceAgencyId);
      if (pdfFile) form.append('file', pdfFile, sanitizeFilename(pdfFile.name));
      else form.append('csv_text', recipientsCsv.trim());
      const { data } = await api.post('/certification/real-member/prepare', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setReview(data);
      const excluded = data.excluded_count ?? data.invalid ?? 0;
      if (data.ok && excluded > 0) toast.warning(`Extracted ${data.source_count ?? data.received} source members — ${data.selected_for_cert_count ?? data.valid} selected, ${excluded} invalid excluded (review + acknowledge before confirming)`);
      else if (data.ok) toast.success(`Extracted ${data.valid} valid member(s) — review before confirming`);
      else toast.error(`Extraction refused: ${data.selected_for_cert_count ?? data.valid} valid after excluding ${excluded} invalid / ${(data.document_errors || []).length + (data.header_errors || []).length} document issue(s)`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = typeof detail === 'string'
        ? detail
        : detail?.error ? `${detail.error}: ${detail.message || ''}` : 'Extraction failed (fail closed)';
      toast.error(message);
    } finally {
      setPreparing(false);
    }
  }, [sourceAgencyId, pdfFile, recipientsCsv, clearReview]);

  const selectedAgency = useMemo(
    () => agencies.find((a) => a.id === sourceAgencyId) || null,
    [agencies, sourceAgencyId],
  );

  const validation = useMemo(
    () => validateCertCsv(recipientsCsv, {
      sourceAgency: selectedAgency,
      retentionApproved: Boolean(retention && retention.approved),
      storeHealthy: Boolean(retention && retention.store && retention.store.healthy),
      usage,
    }),
    [recipientsCsv, selectedAgency, retention, usage],
  );

  const handleFileSelect = useCallback((e) => {
    const input = e.target;
    const file = input.files && input.files[0];
    setFileError('');
    if (!file) return;
    if (/\.pdf$/i.test(file.name)) {
      if (file.size > PDF_MAX_BYTES) {
        setFileError(`PDF exceeds ${(PDF_MAX_BYTES / 1024 / 1024).toFixed(0)} MB.`);
        input.value = '';
        return;
      }
      setPdfFile(file);
      setRecipientsCsv('');
      setCsvFileName(sanitizeFilename(file.name));
      input.value = '';
      return;
    }
    const guard = checkFile(file);
    if (!guard.ok) {
      setFileError(guard.error);
      setCsvFileName('');
      input.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setPdfFile(null);
      setRecipientsCsv(String(reader.result || ''));
      setCsvFileName(sanitizeFilename(file.name));
      input.value = ''; // drop the browser File reference immediately
    };
    reader.onerror = () => {
      setFileError('Could not read the file.');
      input.value = '';
    };
    reader.readAsText(file);
  }, []);

  const grouped = useMemo(() => {
    const g = { root: [], generated: [] };
    files.forEach((f) => {
      if (f.path.startsWith('generated/')) g.generated.push(f);
      else g.root.push(f);
    });
    return g;
  }, [files]);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="certification-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Certificate size={28} className="text-emerald-600" weight="duotone" />
            ODM 270/271 Certification Package
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Everything you need to reply to OMES EDI Support and get TPID 0022168
            authorized for Production 270 inquiries. Download individual files, grab
            the whole package as .zip, or regenerate the sample batch after ODM
            sends their CERT test recipients.
          </p>
        </div>
        <Button
          onClick={handleDownloadAll}
          disabled={downloadingPath === '__all__' || files.length === 0}
          className="bg-emerald-600 hover:bg-emerald-700 shrink-0"
          data-testid="cert-download-all-btn"
        >
          {downloadingPath === '__all__'
            ? <Spinner size={16} className="animate-spin mr-2" />
            : <FileZip size={16} className="mr-2" />}
          Download all as .zip
        </Button>
      </div>

      <ProductionPreflightPanel />

      <SendToCertPanel />

      <CertPhiStagingPanel />

      {/* Quick "how to send this" reminder */}
      <Card className="p-4 border-emerald-200 bg-emerald-50/50" data-testid="cert-instructions-card">
        <p className="text-sm font-semibold text-emerald-900 mb-2 flex items-center gap-1.5">
          <EnvelopeSimple size={14} weight="fill" /> How to send this to ODM
        </p>
        <ol className="text-xs text-emerald-900/80 space-y-1 list-decimal list-inside">
          <li>Click <strong>Download all as .zip</strong> above.</li>
          <li>Open <code className="bg-white px-1 rounded">omes_edi_270_reply.md</code> and paste the body into a reply-all to the OMES EDI Support message.</li>
          <li>Attach the <code className="bg-white px-1 rounded">.edi</code>, <code className="bg-white px-1 rounded">ODM_270_CERT_TEST_PLAN.md</code>, and <code className="bg-white px-1 rounded">270_CERT_test_run_report.md</code> from the .zip.</li>
          <li>For the real-member CERT run, switch the panel below to <strong>REAL MEMBER</strong> mode, upload the authorized member CSV (usage stays <code className="bg-white px-1 rounded">T</code>) and re-download the fresh .edi.</li>
        </ol>
      </Card>

      {loading ? (
        <Card className="p-12 flex items-center justify-center text-slate-500">
          <Spinner size={20} className="animate-spin mr-2" /> Loading files…
        </Card>
      ) : files.length === 0 ? (
        <Card className="p-8 text-center text-slate-500 text-sm">
          No certification files yet. Click Regenerate below to build the first batch.
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          <Card className="overflow-hidden" data-testid="cert-package-docs-card">
            <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50/60">
              <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Package Documents</p>
              <p className="text-[11px] text-slate-500">Test plan, README, email body — attach to your ODM reply.</p>
            </div>
            <div>
              {grouped.root.map((f) => (
                <FileRow
                  key={f.path}
                  file={f}
                  onDownload={handleDownload}
                  isDownloading={downloadingPath === f.path}
                />
              ))}
            </div>
          </Card>

          <Card className="overflow-hidden" data-testid="cert-generated-artifacts-card">
            <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50/60 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Generated EDI + Report</p>
                <p className="text-[11px] text-slate-500">Regenerate any time from the panel below.</p>
              </div>
              {latestEdi && (
                <Badge variant="outline" className="text-[10px] border-emerald-200 bg-emerald-50 text-emerald-700">
                  Latest: {latestEdi.split('/').pop()}
                </Badge>
              )}
            </div>
            <div>
              {grouped.generated.map((f) => (
                <FileRow
                  key={f.path}
                  file={f}
                  onDownload={handleDownload}
                  isDownloading={downloadingPath === f.path}
                />
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* Regeneration panel */}
      <Card className="p-4" data-testid="cert-regenerate-card">
        <div className="flex items-center gap-2 mb-3">
          <ArrowsClockwise size={18} className="text-slate-600" />
          <h2 className="text-sm font-semibold text-slate-900">Regenerate certification batch</h2>
        </div>
        <p className="text-xs text-slate-600 mb-3">
          Runs <code className="bg-slate-100 px-1 rounded font-mono">tools.build_certification_270</code> in the
          <strong> explicitly selected mode</strong>. There is no silent fallback: a REAL MEMBER run with no CSV is
          refused, and the synthetic structure run never accepts member data.
        </p>

        <div className="mb-3 grid gap-2 sm:grid-cols-2" data-testid="cert-mode-selector">
          <label
            className={`flex items-start gap-2 rounded-md border p-2.5 text-xs cursor-pointer ${!realMember ? 'border-slate-900 bg-slate-50' : 'border-slate-200'}`}
          >
            <input
              type="radio"
              name="cert-mode"
              value="synthetic_structure"
              checked={!realMember}
              onChange={() => setMode('synthetic_structure')}
              className="mt-0.5"
              data-testid="cert-mode-synthetic"
            />
            <span>
              <strong>SYNTHETIC / STRUCTURE-ONLY</strong>
              <br />
              <span className="text-slate-600">
                Built-in 7-scenario <code className="bg-slate-100 px-1 rounded font-mono">OH_TEST_*</code> matrix.
                X12 structure check only — never transmittable, refuses member data.
              </span>
            </span>
          </label>
          <label
            className={`flex items-start gap-2 rounded-md border p-2.5 text-xs cursor-pointer ${realMember ? 'border-rose-600 bg-rose-50/60' : 'border-slate-200'}`}
          >
            <input
              type="radio"
              name="cert-mode"
              value="real_member"
              checked={realMember}
              onChange={() => setMode('real_member')}
              className="mt-0.5"
              data-testid="cert-mode-real-member"
            />
            <span>
              <strong className="text-rose-700">REAL MEMBER</strong>
              <br />
              <span className="text-slate-600">
                Operator-supplied CSV of real OMES-enrolled members. Requires usage <strong>T</strong>, the locked
                source agency, ≥ {MIN_INQUIRIES} valid rows, no synthetic ids, no padding. Fails closed without a CSV.
              </span>
            </span>
          </label>
        </div>

        <div className="flex gap-2 mb-3">
          <label className="flex items-center gap-1.5 text-xs">
            <input
              type="radio"
              name="usage"
              value="T"
              checked={usage === 'T'}
              onChange={(e) => setUsage(e.target.value)}
              data-testid="cert-usage-T"
            />
            <span>Usage <strong>T</strong> (CERT sandbox)</span>
          </label>
          <label className="flex items-center gap-1.5 text-xs">
            <input
              type="radio"
              name="usage"
              value="P"
              checked={usage === 'P'}
              onChange={(e) => setUsage(e.target.value)}
              disabled={realMember}
              data-testid="cert-usage-P"
            />
            <span className={realMember ? 'text-slate-400' : ''}>
              Usage <strong>P</strong> (PROD — only after ODM sign-off{realMember ? '; not allowed in REAL MEMBER mode' : ''})
            </span>
          </label>
        </div>

        {realMember && (
        <>
        <div className="mb-2">
          <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5 mb-1" htmlFor="cert-csv-file">
            <UploadSimple size={14} className="text-slate-500" /> Upload the member source — PDF patient report or CSV
          </label>
          <input
            id="cert-csv-file"
            type="file"
            accept=".pdf,application/pdf,.csv,text/csv"
            onChange={handleFileSelect}
            className="block w-full text-xs text-slate-700 file:mr-3 file:rounded-md file:border-0 file:bg-slate-900 file:px-3 file:py-1.5 file:text-white file:text-xs file:cursor-pointer hover:file:bg-slate-800"
            data-testid="cert-csv-file-input"
          />
          {csvFileName && (
            <p className="text-[11px] text-emerald-700 mt-1" data-testid="cert-csv-file-name">
              Loaded: {csvFileName}{pdfFile ? ' (PDF — native text extraction, no OCR)' : ''} — click <strong>Extract &amp; validate for review</strong> to continue.
            </p>
          )}
          {fileError && (
            <p className="text-[11px] text-rose-600 mt-1" data-testid="cert-csv-file-error">{fileError}</p>
          )}
          <p className="text-[11px] text-slate-500 mt-1">
            .pdf (max {(PDF_MAX_BYTES / 1024 / 1024).toFixed(0)} MB, native text only — scanned/image PDFs are refused, OCR is not enabled) or .csv
            (max {(MAX_FILE_BYTES / 1024 / 1024).toFixed(0)} MB). Selecting a file only holds it in this browser tab. Extraction sends it to the server
            for in-memory parsing and canonical validation — nothing is persisted, staged or transmitted until you confirm the reviewed population.
          </p>
          <div className="flex items-center gap-2 my-2 text-[11px] font-semibold text-slate-400">
            <span className="h-px flex-1 bg-slate-200" /> OR paste CSV manually <span className="h-px flex-1 bg-slate-200" />
          </div>
        </div>

        <Textarea
          value={recipientsCsv}
          onChange={(e) => { setPdfFile(null); setRecipientsCsv(e.target.value); }}
          placeholder={`medicaid_id,first_name,last_name,dob,gender
############,FIRST,LAST,YYYY-MM-DD,F
...`}
          rows={5}
          className="font-mono text-xs mb-2"
          data-testid="cert-recipients-csv"
        />

        <p className="text-[11px] text-slate-600 mb-3" data-testid="cert-real-member-csv-help">
          <strong>PDF report</strong> (Comfort and Passionate patient report, label/value blocks): <em>Patient Name</em> (LAST, FIRST) → names,{' '}
          <em>DOB</em> (MM/DD/YYYY) → dob, <em>Sex</em> (M/F) → gender, <em>Payer &amp; Insurance ID</em> entries{' '}
          <code className="bg-slate-100 px-1 rounded font-mono">Plan (ID# …)</code> → medicaid_id (all payer IDs must agree; the internal{' '}
          <em>Patient ID</em> is never used). Anything ambiguous or missing is refused — never inferred or repaired.
          <br />
          <strong>CSV</strong> carries <strong>exactly</strong> five columns:{' '}
          <code className="bg-slate-100 px-1 rounded font-mono">medicaid_id</code> (12 digits),{' '}
          <code className="bg-slate-100 px-1 rounded font-mono">first_name</code>,{' '}
          <code className="bg-slate-100 px-1 rounded font-mono">last_name</code>,{' '}
          <code className="bg-slate-100 px-1 rounded font-mono">dob</code> (YYYY-MM-DD or CCYYMMDD),{' '}
          <code className="bg-slate-100 px-1 rounded font-mono">gender</code>.
          Both formats converge on the same canonical population and the same mandatory review. Eligibility is determined from ODM&apos;s returned 271 — never predicted here.
          <br />
          <span className="text-slate-500">
            <code className="bg-slate-100 px-1 rounded font-mono">case_id</code>,{' '}
            <code className="bg-slate-100 px-1 rounded font-mono">scenario</code>,{' '}
            <code className="bg-slate-100 px-1 rounded font-mono">expected_271_response</code> and any{' '}
            <code className="bg-slate-100 px-1 rounded font-mono">OH_TEST_*</code> identifier are synthetic-test
            artefacts and are <strong>refused</strong> by the server in REAL MEMBER mode.
          </span>
        </p>
        </>
        )}

        <div className="mb-3 space-y-2 rounded-md border border-slate-200 bg-slate-50/60 p-3">
          <CertSourceAgencyLockBanner lock={sourceLock} />
          <label className="text-xs font-semibold text-slate-700 block">
            Source agency (owner of the member population)
          </label>
          <select
            value={sourceAgencyId}
            onChange={(e) => setSourceAgencyId(e.target.value)}
            className="w-full max-w-md rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs"
            data-testid="cert-source-agency-select"
          >
            <option value="">— select source agency —</option>
            {agencies.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}{a.npi ? ` (NPI ${a.npi})` : ' (no NPI on profile)'}
              </option>
            ))}
          </select>
          <p className="text-[11px] text-slate-500">
            Every CERT run belongs to exactly ONE source agency — REQUIRED for every
            generation, synthetic matrix runs included. The generated 270&apos;s provider identity
            (Loop 2100B) uses the selected agency&apos;s check-digit-validated NPI — never a
            global, seeded or placeholder fallback. AZAI remains the X12 sender/trading partner (ISA06).
          </p>
          {realMember && (
            <label className="flex items-start gap-2 text-[11px] text-slate-700">
              <input
                type="checkbox"
                checked={populationAttested}
                onChange={(e) => setPopulationAttested(e.target.checked)}
                className="mt-0.5"
                data-testid="cert-population-attestation-checkbox"
              />
              <span>
                I attest that every member in this uploaded source is an OMES-enrolled member of the
                selected source agency ONLY — no mixed-agency population. Recorded in the
                run&apos;s audit provenance.
              </span>
            </label>
          )}
        </div>

        {realMember && !sourcePresent && (
          <div className="mb-3 rounded-md border border-rose-200 bg-rose-50 p-3 text-[11px] text-rose-700" data-testid="cert-real-member-csv-required">
            REAL MEMBER mode requires an operator-supplied member source (PDF report or CSV). The server refuses a run
            without one (<code className="font-mono">REAL_MEMBER_CSV_REQUIRED</code>) and without a reviewed population
            (<code className="font-mono">REAL_MEMBER_REVIEW_REQUIRED</code>) — it never falls back to the synthetic matrix.
          </div>
        )}

        {realMember && csvPresent && !pdfFile && (
          <div className="mb-3 rounded-md border border-slate-200 bg-white p-3" data-testid="cert-csv-validation-summary">
            <h3 className="text-xs font-semibold text-slate-800 mb-2 flex items-center gap-1.5">
              <ShieldCheck size={14} className="text-slate-500" /> PHI-safe validation summary
            </h3>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-slate-700 sm:grid-cols-3">
              <SummaryRow label="File" value={csvFileName || '(pasted manually)'} />
              <SummaryRow label="Rows received" value={validation.received} />
              <SummaryRow label="Valid inquiries" value={validation.valid} tone={validation.valid > 0 ? 'ok' : 'warn'} />
              <SummaryRow label="Invalid inquiries" value={validation.invalid} tone={validation.invalid === 0 ? 'ok' : 'bad'} />
              <SummaryRow label="Duplicates" value={validation.duplicates} tone={validation.duplicates === 0 ? 'ok' : 'bad'} />
              <SummaryRow label="Empty rows ignored" value={validation.emptyRowsIgnored} />
              <SummaryRow label="Source agency" value={selectedAgency ? selectedAgency.name : '— none selected —'} tone={validation.agencyActive ? 'ok' : 'bad'} />
              <SummaryRow label="Provider NPI" value={selectedAgency && selectedAgency.npi ? selectedAgency.npi : '—'} tone={validation.agencyNpiValid ? 'ok' : 'bad'} />
              <SummaryRow label="Usage" value={usage === 'T' ? 'T (CERT)' : `${usage} (NOT CERT)`} tone={validation.usageIsCert ? 'ok' : 'bad'} />
              <SummaryRow label={`Minimum ${MIN_INQUIRIES} inquiries`} value={validation.minSatisfied ? 'PASS' : 'FAIL'} tone={validation.minSatisfied ? 'ok' : 'bad'} />
              <SummaryRow label="Retention policy" value={validation.retentionApproved ? 'APPROVED' : 'NOT APPROVED'} tone={validation.retentionApproved ? 'ok' : 'bad'} />
              <SummaryRow label="Encrypted CERT store" value={validation.storeHealthy ? 'HEALTHY' : 'UNHEALTHY'} tone={validation.storeHealthy ? 'ok' : 'bad'} />
              <SummaryRow label="Transmission" value="LOCKED" tone="ok" />
            </dl>

            {validation.headerErrors.length > 0 && (
              <ul className="mt-2 space-y-0.5" data-testid="cert-csv-header-errors">
                {validation.headerErrors.map((h, i) => (
                  <li key={i} className="text-[11px] text-rose-600">• {h}</li>
                ))}
              </ul>
            )}

            {validation.rowErrors.length > 0 && (
              <div className="mt-2" data-testid="cert-csv-row-errors">
                <p className="text-[11px] font-semibold text-rose-700 mb-1">
                  {validation.invalid + validation.duplicates} row issue(s) — fix and re-upload (no member values are shown):
                </p>
                <ul className="max-h-40 overflow-auto space-y-0.5">
                  {validation.rowErrors.slice(0, 100).map((e, i) => (
                    <li key={i} className="text-[11px] text-rose-600 font-mono">
                      Row {e.row} — {e.field} {e.message}
                    </li>
                  ))}
                  {validation.rowErrors.length > 100 && (
                    <li className="text-[11px] text-slate-500">…and {validation.rowErrors.length - 100} more.</li>
                  )}
                </ul>
              </div>
            )}

            <p
              className={`mt-2 text-[11px] font-semibold ${validation.canGenerate && populationAttested ? 'text-emerald-700' : 'text-amber-700'}`}
              data-testid="cert-csv-gate-status"
            >
              {validation.canGenerate
                ? 'Local pre-check PASS — extract & validate on the server to open the mandatory review.'
                : validation.canGenerateWithExclusions
                  ? `Local pre-check: ${validation.invalid + validation.duplicates} invalid/duplicate row(s) will be marked INVALID — NOT ELIGIBLE FOR CERT and require explicit exclusion in the server review (${validation.valid} valid remain ≥ ${MIN_INQUIRIES}).`
                  : 'Local pre-check FAIL — the server review will refuse this file as well; fix it first.'}
            </p>
          </div>
        )}

        {realMember && (
          <div className="mb-3 flex items-center gap-2 flex-wrap">
            <Button
              onClick={handlePrepare}
              disabled={preparing || !sourcePresent || !sourceAgencyId || lockBlocksSelection || lockRecordUnbound}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="cert-prepare-btn"
            >
              {preparing ? <Spinner size={16} className="animate-spin mr-2" /> : <ShieldCheck size={16} className="mr-2" />}
              Extract &amp; validate for review
            </Button>
            <span className="text-[11px] text-slate-500" data-testid="cert-prepare-hint">
              {lockRecordUnbound
                ? `REAL MEMBER runs are bound to ${sourceLock?.name || 'the locked production agency'} in every environment — that record is not bound here, so extraction is refused (fail closed).`
                : !sourceAgencyId
                  ? 'Select the locked source agency first — the server binds the agency before it reads any member data.'
                  : !sourcePresent
                    ? 'Upload the PDF report / CSV or paste CSV to enable extraction.'
                    : review
                      ? 'Population extracted — review every row below, then confirm.'
                      : 'Server-side native extraction + canonical five-field validation; opens the mandatory review.'}
            </span>
          </div>
        )}

        {realMember && review && (
          <>
            <CertPopulationReview review={review} agencyName={selectedAgency ? selectedAgency.name : ''} />
            {review.requires_exclusions && (
              <label className="mb-2 flex items-start gap-2 text-[11px] text-amber-900 rounded border border-amber-200 bg-amber-50 p-2" data-testid="cert-exclusions-ack-label">
                <input
                  type="checkbox"
                  checked={exclusionsAcknowledged}
                  disabled={!review.ok}
                  onChange={(e) => setExclusionsAcknowledged(e.target.checked)}
                  className="mt-0.5"
                  data-testid="cert-exclusions-ack-checkbox"
                />
                <span>
                  I acknowledge that {review.excluded_count} of {review.source_count} source member(s) failed validation
                  ({Object.entries(review.exclusion_reason_counts || {}).map(([k, v]) => `${k} ×${v}`).join(', ') || 'see rows above'}),
                  are excluded from the CERT population and will NOT be transmitted. Exactly {review.selected_for_cert_count} selected
                  members will be staged (exclusion digest {String(review.exclusion_digest || '').slice(0, 16)}…).
                </span>
              </label>
            )}
            <label className="mb-3 flex items-start gap-2 text-[11px] text-slate-700" data-testid="cert-review-confirm-label">
              <input
                type="checkbox"
                checked={reviewConfirmed}
                disabled={!review.ok}
                onChange={(e) => setReviewConfirmed(e.target.checked)}
                className="mt-0.5"
                data-testid="cert-review-confirm-checkbox"
              />
              <span>
                I have reviewed the extracted population above against the source document and confirm the source agency
                ({selectedAgency ? selectedAgency.name : '—'}), the selected member count ({review.selected_for_cert_count ?? review.valid}
                {review.requires_exclusions ? ` of ${review.source_count} source members` : ''}), every Medicaid ID, name, DOB and sex value.
                Staging binds exactly this selected population (digest {String(review.population_digest || '').slice(0, 16)}…).
              </span>
            </label>
          </>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={handleRegenerate}
            disabled={
              regenerating
              || !sourceAgencyId
              || !validation.agencyNpiValid
              || lockBlocksSelection
              || lockRecordUnbound
              || (realMember && !(review && review.ok && review.review_token && reviewConfirmed && populationAttested
                && (!review.requires_exclusions || exclusionsAcknowledged)))
            }
            className={realMember ? 'bg-rose-700 hover:bg-rose-800' : 'bg-slate-900 hover:bg-slate-800'}
            data-testid="cert-regenerate-btn"
          >
            {regenerating
              ? <Spinner size={16} className="animate-spin mr-2" />
              : <ArrowsClockwise size={16} className="mr-2" />}
            {realMember ? 'Confirm reviewed population & stage REAL MEMBER batch' : 'Generate synthetic structure batch'}
          </Button>
          <span className="text-[11px] text-slate-500" data-testid="cert-regenerate-hint">
            {!sourceAgencyId
              ? 'Select a source agency with a valid NPI \u2014 generation is refused without an explicitly bound provider identity.'
              : lockBlocksSelection
                ? `Selected agency is not the operator-locked CERT source agency (${sourceLock.name}) \u2014 generation refused.`
                : lockRecordUnbound
                ? `REAL MEMBER runs are bound to ${sourceLock.name || 'the locked production agency'} in every environment \u2014 that record is not bound here (${sourceLock.conflict || sourceLock.record?.reason || 'not found'}), so staging is refused (fail closed).`
                : !validation.agencyNpiValid
                ? 'Selected agency NPI fails the CMS check-digit validation \u2014 fix the agency profile first.'
                : realMember
                  ? (!review
                    ? 'Nothing can be staged until the population has been extracted and reviewed (mandatory review gate).'
                    : !review.ok
                      ? (review.result === 'REFUSED_MIN_NOT_MET'
                        ? `REFUSED \u2014 only ${review.selected_for_cert_count ?? review.valid} valid unique member(s) remain after exclusions (minimum ${review.min_inquiries ?? MIN_INQUIRIES}). Correct the source and extract again.`
                        : 'The extracted population was refused \u2014 correct the source and extract again.')
                      : review.requires_exclusions && !exclusionsAcknowledged
                        ? `Acknowledge the ${review.excluded_count} excluded invalid member(s) to enable staging of the ${review.selected_for_cert_count} selected members.`
                        : !reviewConfirmed
                          ? 'Tick the review confirmation to enable staging.'
                          : !populationAttested
                            ? 'Tick the single-agency population attestation to enable staging.'
                            : `Staging will bind exactly the ${review.selected_for_cert_count ?? review.valid} selected members (server re-validates the digest${review.requires_exclusions ? ', the acknowledged exclusions' : ''}, agency and operator).`)
                  : 'SYNTHETIC / STRUCTURE-ONLY \u2014 built-in 7-scenario OH_TEST_* matrix (never transmittable).'}
          </span>
        </div>

        {regenOutput && (
          <pre
            className="mt-3 p-3 bg-slate-950 text-emerald-300 text-[11px] font-mono rounded overflow-x-auto"
            data-testid="cert-regenerate-output"
          >
            {regenOutput}
          </pre>
        )}
      </Card>
    </div>
  );
};

export default CertificationPage;
